import axios from "axios";
import { useFormik } from "formik";
import React, { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaAward,
  FaCertificate,
  FaCheck,
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaChevronDown,
  FaCopy,
  FaDownload,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  AGE_RANGE,
  EDUCATION_LEVELS,
  EMPLOYMENT_STATUS,
  FIELDS,
  GENDER,
  NATIONALITIES,
  SECTORS,
  TYPES,
  UNIVERSITIES,
} from "../constants/common";
import { useAuth } from "../context/AuthContext";
import i18n from "../i18n";

const Profile = () => {
  const { t } = useTranslation();
  const { user, login, setUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCertificatesModal, setShowCertificatesModal] = useState(false);
  const [serverMessage, setServerMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();

  const fullName = `${user.first_name} ${user.second_name} ${user.third_name} ${user.last_name}`;

  // --- Profile Formik ---
  const profileFormik = useFormik({
    initialValues: {
      firstName: user?.first_name || "",
      secondName: user?.second_name || "",
      thirdName: user?.third_name || "",
      lastName: user?.last_name || "",
      phone: user?.phone ? user?.phone?.replace(/^962/, "") : "",
      email: user?.email || "",
      ageRange: user?.age_range_id || "",
      gender: user?.gender_id || "",
      city: user?.governorate?.id || "",
      studentType: user?.type || "",
      university: user?.university_id || "",
      universityName: user?.university_name || "",
      fieldOfStudy: user?.other_major_id || user?.student_major_id || "",
      fieldOfStudyOther: user?.other_major_name || user?.student_major_name || "",
      employmentStatus: user?.employment_status_id || "",
      employmentStatusOther: user?.employment_status || "",
      educationLevel: user?.education_level_id || "",
      sector: user?.sector_id || "",
      ministerialID: user?.ministerial_id || "",
      nationality:
        user?.nationality_id == NATIONALITIES.JORDAN
          ? NATIONALITIES.JORDAN
          : user?.nationality_id == NATIONALITIES.SYRIA
            ? NATIONALITIES.SYRIA
            : NATIONALITIES.OTHER,
      nationalNumber: user?.national_id || "",
      documentId: user?.national_id || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required(t("required")),
      secondName: Yup.string().required(t("required")),
      thirdName: Yup.string().required(t("required")),
      lastName: Yup.string().required(t("required")),

      email: Yup.string().email(t("invalidEmail")).required(t("required")),

      phone: Yup.string().required(t("required")),

      ageRange: Yup.number().nullable().typeError(t("required")).required(t("required")),

      gender: Yup.number().nullable().typeError(t("required")).required(t("required")),

      city: Yup.string().required(t("required")),

      studentType: Yup.number().nullable().typeError(t("required")).required(t("required")),

      // --- University ---
      university: Yup.number().when("studentType", {
        is: (val) => val == TYPES.UNIVERSITY,
        then: (schema) => schema.nullable().typeError(t("required")).required(t("required")),
        otherwise: (schema) => schema.nullable(),
      }),

      // --- Field of Study ---
      fieldOfStudy: Yup.number().when("studentType", {
        is: (val) => val == TYPES.UNIVERSITY || val == TYPES.OTHER,
        then: (schema) => schema.nullable().typeError(t("required")).required(t("required")),
        otherwise: (schema) => schema.nullable(),
      }),

      // --- Employment ---
      employmentStatus: Yup.number().when("studentType", {
        is: (val) => val == TYPES.OTHER,
        then: (schema) => schema.nullable().typeError(t("required")).required(t("required")),
        otherwise: (schema) => schema.nullable(),
      }),

      sector: Yup.number().when(["studentType", "employmentStatus"], {
        is: (type, status) => type == TYPES.OTHER && status == 1,
        then: (schema) => schema.nullable().typeError(t("required")).required(t("required")),
        otherwise: (schema) => schema.nullable(),
      }),

      educationLevel: Yup.number().when("studentType", {
        is: (val) => val == TYPES.OTHER,
        then: (schema) => schema.nullable().typeError(t("required")).required(t("required")),
        otherwise: (schema) => schema.nullable(),
      }),

      // --- Teacher ---
      ministerialID: Yup.string().when("studentType", {
        is: (val) => val == TYPES.SCHOOLTEACHER,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.nullable(),
      }),

      // --- Nationality ---
      nationality: Yup.number().nullable().typeError(t("required")).required(t("required")),

      nationalNumber: Yup.string().when("nationality", {
        is: (val) => val == NATIONALITIES.JORDAN,
        then: (schema) => schema.required(t("required")).matches(/^\d{10}$/, t("idMustBe10Digits")),
        otherwise: (schema) => schema.nullable(),
      }),

      documentId: Yup.string().when("nationality", {
        is: (val) => val == NATIONALITIES.SYRIA,
        then: (schema) => schema.required(t("required")).matches(/^\d{10}$/, t("idMustBe10Digits")),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          // --- Basic Info ---
          first_name: values.firstName,
          second_name: values.secondName,
          third_name: values.thirdName,
          last_name: values.lastName,
          phone: `962${values.phone.replace(/^0/, "")}`,
          email: values.email,
          age_range_id: values.ageRange,
          gender_id: values.gender,
          governorate_id: values.city,
          type: values.studentType,

          // --- University ---
          ...(values.studentType == TYPES.UNIVERSITY && {
            university_id: values.university || null,
            ...(values.university == 13 && {
              university_name: values.universityName,
            }),
          }),

          // --- Majors ---
          student_major_id: null,
          student_major_name: null,
          other_major_id: null,
          other_major_name: null,

          ...(values.studentType == TYPES.UNIVERSITY && {
            student_major_id: values.fieldOfStudy || null,
            ...(values.fieldOfStudy == 12 && {
              student_major_name: values.fieldOfStudyOther,
            }),
          }),

          ...(values.studentType == TYPES.OTHER && {
            other_major_id: values.fieldOfStudy || null,
            ...(values.fieldOfStudy == 12 && {
              other_major_name: values.fieldOfStudyOther,
            }),
          }),

          // --- Employment ---
          ...(values.studentType == TYPES.OTHER && {
            employment_status_id: values.employmentStatus,

            ...(values.employmentStatus == 3 && {
              employment_status: values.employmentStatusOther,
            }),

            ...(values.employmentStatus == 1 && {
              sector_id: values.sector,
            }),

            education_level_id: values.educationLevel || null,
          }),

          // --- Nationality ---
          nationality_id: values.nationality,
          ministerial_id: values.ministerialID,

          national_id:
            values.nationality == NATIONALITIES.JORDAN
              ? values.nationalNumber
              : values.nationality == NATIONALITIES.SYRIA
                ? values.documentId
                : null,
        };

        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/auth/profile`, payload, {
          headers: {
            Authorization: `Bearer ${user.token}`,

            lang: i18n.language,
          },
        });

        login({ ...user, ...payload });
        setIsEditing(false);
      } catch (err) {
        if (err.response?.status == 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else console.error(err);
      }
    },
  });

  useEffect(() => {
    if (profileFormik.values.employmentStatus != 3) {
      profileFormik.setFieldValue("employmentStatusOther", "");
    }
  }, [profileFormik.values.employmentStatus]);

  useEffect(() => {
    if (profileFormik.values.fieldOfStudy != 12) {
      profileFormik.setFieldValue("fieldOfStudyOther", "");
    }
  }, [profileFormik.values.fieldOfStudy]);

  // --- Change Password Formik ---
  const passwordFormik = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
    validationSchema: Yup.object({
      current_password: Yup.string().required(t("required")),
      new_password: Yup.string()
        .min(8, t("minPassword", { count: 8 }))
        .max(20, t("field_max_20"))
        .required(t("required")),
      new_password_confirmation: Yup.string()
        .oneOf([Yup.ref("new_password"), null], t("passwordsMustMatch"))
        .required(t("required")),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      setServerMessage("");
      try {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/auth/change-password`, values, {
          headers: {
            Authorization: `Bearer ${user.token}`,
            lang: i18n.language,
          },
        });
        setServerMessage("✅ Password changed successfully!");
        resetForm();
        setTimeout(() => {
          setShowChangePassword(false);
          setServerMessage("");
        }, 1200);
      } catch (err) {
        if (err.response?.status == 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          setServerMessage(err.response?.data?.message || "❌ Failed to change password");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleDownloadCertificate = async (certificateId) => {
    try {
      setDownloadingId(certificateId);

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/certificate/${certificateId}`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `certificate-${certificateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading certificate:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  // Fetch governorates
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/governorates`, {
          headers: {
            lang: i18n.language,
          },
        });
        setCities(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    const fetchCertificats = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/certificates`, {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });
        setCertificates(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
    fetchCertificats();
  }, [i18n.language]);

  const handleCopyRefCode = () => {
    const textarea = document.createElement("textarea");
    textarea.value = user.referral_code;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  // Error message helper
  const ErrorMsg = ({ name }) =>
    profileFormik.touched[name] && profileFormik.errors[name] ? (
      <div className="text-sm text-red-500">{profileFormik.errors[name]}</div>
    ) : null;

  return (
    <>
      <div className="mx-auto max-w-6xl space-y-6 p-4 pt-0" dir={i18n.language == "ar" ? "rtl" : "ltr"}>
        {/* Back button + Logo */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-purple-500 hover:text-purple-700"
          >
            {i18n.language == "ar" ? (
              <FaChevronCircleRight size={18} className="text-purple-500" />
            ) : (
              <FaChevronCircleLeft size={18} className="text-purple-500" />
            )}
            {t("back")}
          </button>
          <img
            src="/logo-blue.svg"
            alt="Logo"
            onClick={() => navigate("/")}
            className="h-10 w-auto cursor-pointer"
          />
        </div>
        {/* Header */}
        <div className="flex flex-col items-center gap-4 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-6 text-white shadow-md sm:flex-row sm:gap-6">
          <div className="flex w-full flex-col items-center text-center sm:block sm:text-left">
            <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-2xl font-bold capitalize">{fullName}</h1>

              {/* Referral Code */}
              <div className="flex flex-col items-center gap-1 sm:items-end">
                <span className="text-xs text-white/80">{t("yourRefCode")}</span>
                <div
                  onClick={handleCopyRefCode}
                  className="relative flex cursor-pointer items-center gap-2 rounded-lg bg-white/20 px-3 py-1 font-semibold text-purple-900 shadow-md transition hover:bg-white/30"
                >
                  <span className="text-white/90">{user?.referral_code}</span>
                  {copied ? <FaCheck className="text-green-400" /> : <FaCopy className="text-white/90" />}

                  {/* Tooltip */}
                  {copied && (
                    <span className="absolute -top-6 left-1/2 w-16 -translate-x-1/2 rounded bg-black/80 px-2 py-1 text-xs text-white shadow-lg">
                      {t("copied")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm hover:bg-white/30"
                >
                  <FaEdit size={14} /> {t("editProfile")}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={profileFormik.handleSubmit}
                    className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600"
                  >
                    <FaSave size={14} /> {t("save")}
                  </button>
                  <button
                    onClick={() => {
                      profileFormik.resetForm();
                      setIsEditing(false);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600"
                  >
                    <FaTimes size={14} /> {t("cancel")}
                  </button>
                </div>
              )}
              {certificates?.length ? (
                <button
                  onClick={() => setShowCertificatesModal(true)}
                  className="group flex items-center gap-3 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-400 px-6 py-2.5 font-semibold text-purple-800 shadow-md transition-all duration-200 hover:from-yellow-400 hover:to-yellow-500 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-yellow-300 active:scale-95"
                >
                  <FaCertificate className="text-lg text-purple-700 transition group-hover:scale-110" />

                  <span>{i18n.language == "ar" ? "شهاداتك" : "Your Certificates"}</span>

                  {/* helper text */}
                  <span className="hidden text-xs font-normal text-purple-700 opacity-70 sm:inline">
                    {i18n.language == "ar" ? "تحميل الشهادات" : "Download certificates"}
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={profileFormik.handleSubmit}
          className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
        >
          <h2 className="mb-4 text-lg font-semibold text-gray-900">{t("personalInformation")}</h2>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
            {/* First Name */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("firstNameLabel")}</label>
                <ErrorMsg name="firstName" />
              </div>
              {isEditing ? (
                <input
                  type="text"
                  name="firstName"
                  value={profileFormik.values.firstName}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2"
                />
              ) : (
                <p className="mt-1 text-gray-800">{user.first_name}</p>
              )}
            </div>

            {/* Second Name */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("secondNameLabel")}</label>
                <ErrorMsg name="secondName" />
              </div>{" "}
              {isEditing ? (
                <input
                  type="text"
                  name="secondName"
                  value={profileFormik.values.secondName}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2"
                />
              ) : (
                <p className="mt-1 text-gray-800">{user.second_name}</p>
              )}
            </div>

            {/* Third Name */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("thirdNameLabel")}</label>
                <ErrorMsg name="thirdName" />
              </div>{" "}
              {isEditing ? (
                <input
                  type="text"
                  name="thirdName"
                  value={profileFormik.values.thirdName}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2"
                />
              ) : (
                <p className="mt-1 text-gray-800">{user.third_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("lastNameLabel")}</label>
                <ErrorMsg name="lastName" />
              </div>{" "}
              {isEditing ? (
                <input
                  type="text"
                  name="lastName"
                  value={profileFormik.values.lastName}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2"
                />
              ) : (
                <p className="mt-1 text-gray-800">{user.last_name}</p>
              )}
              <ErrorMsg name="lastName" />
            </div>

            {/* Email */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("emailLabel")}</label>
                <ErrorMsg name="email" />
              </div>
              {isEditing ? (
                <input
                  readOnly
                  disabled
                  type="email"
                  name="email"
                  value={profileFormik.values.email}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-200 p-2"
                />
              ) : (
                <p className="mt-1 text-gray-800">{user.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("phoneLabel")}</label>
                <ErrorMsg name="phone" />
              </div>{" "}
              {isEditing ? (
                <div className="flex" dir="ltr">
                  <span className="flex items-center rounded-l-xl border border-r-0 bg-gray-200 px-3">
                    +962
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    value={profileFormik.values.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      profileFormik.setFieldValue("phone", value.slice(0, 10));
                    }}
                    onBlur={profileFormik.handleBlur}
                    className="w-full rounded-r-xl border px-3 py-2"
                  />
                </div>
              ) : (
                <p className="mt-1 text-gray-800">
                  {i18n.language == "ar" ? `${user.phone}+` : `+${user.phone}`}
                </p>
              )}
            </div>

            {/* Age Range */}
            <div className="relative">
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("ageRangeLabel")}</label>
                <ErrorMsg name="ageRange" />
              </div>{" "}
              {isEditing ? (
                <select
                  name="ageRange"
                  value={profileFormik.values.ageRange}
                  onChange={(e) => profileFormik.setFieldValue("ageRange", Number(e.target.value))}
                  onBlur={profileFormik.handleBlur}
                  className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                >
                  <option value="" disabled>
                    {t("ageRangePlaceholder")}
                  </option>
                  <option value={AGE_RANGE.TEEN}>13 - 17</option>
                  <option value={AGE_RANGE.YOUNG}>18 - 34</option>
                  <option value={AGE_RANGE.ADULT}>34+</option>
                </select>
              ) : (
                <p className="mt-1 text-gray-800">
                  {user.age_range_id == AGE_RANGE.TEEN
                    ? "13 - 17"
                    : user.age_range_id == AGE_RANGE.YOUNG
                      ? "18 - 34"
                      : user.age_range_id == AGE_RANGE.ADULT
                        ? "34+"
                        : "-"}
                </p>
              )}
              {isEditing && (
                <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
              )}{" "}
            </div>

            {/* Gender */}
            <div className="relative">
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("genderLabel")}</label>
                <ErrorMsg name="gender" />
              </div>{" "}
              {isEditing ? (
                <select
                  name="gender"
                  value={profileFormik.values.gender}
                  onChange={(e) => profileFormik.setFieldValue("gender", Number(e.target.value))}
                  onBlur={profileFormik.handleBlur}
                  className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                >
                  <option value="" disabled>
                    {t("genderPlaceholder")}
                  </option>
                  <option value={GENDER.MALE}>{t("male")}</option>
                  <option value={GENDER.FEMALE}>{t("female")}</option>
                </select>
              ) : (
                <p className="mt-1 text-gray-800">
                  {user.gender_id == GENDER.MALE
                    ? t("male")
                    : user.gender_id == GENDER.FEMALE
                      ? t("female")
                      : "-"}
                </p>
              )}
              {isEditing && (
                <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
              )}{" "}
            </div>

            {/* City */}
            <div className="relative">
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("cityLabel")}</label>
                <ErrorMsg name="city" />
              </div>{" "}
              {isEditing ? (
                <select
                  name="city"
                  value={profileFormik.values.city}
                  onChange={profileFormik.handleChange}
                  onBlur={profileFormik.handleBlur}
                  className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                >
                  <option value="" disabled>
                    {t("cityPlaceholder")}
                  </option>
                  {cities?.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="mt-1 text-gray-800">{user.governorate?.name}</p>
              )}
              {isEditing && (
                <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
              )}{" "}
            </div>
            {/* Student Type */}
            <div className="relative">
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("studentTypeLabel")}</label>
                <ErrorMsg name="studentType" />
              </div>{" "}
              {isEditing ? (
                <select
                  name="studentType"
                  value={profileFormik.values.studentType}
                  onChange={(e) => profileFormik.setFieldValue("studentType", Number(e.target.value))}
                  onBlur={profileFormik.handleBlur}
                  className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                >
                  <option value="" disabled>
                    {t("studentTypePlaceholder")}
                  </option>
                  <option value={TYPES.SCHOOL}>{t("schoolStudent")}</option>
                  <option value={TYPES.UNIVERSITY}>{t("universityStudent")}</option>
                  <option value={TYPES.SCHOOLTEACHER}> {t("schoolTeacher")}</option>
                  <option value={TYPES.OTHER}>{t("other")}</option>
                </select>
              ) : (
                <p className="mt-1 text-gray-800">
                  {user.type == TYPES.SCHOOL
                    ? t("schoolStudent")
                    : user.type == TYPES.UNIVERSITY
                      ? t("universityStudent")
                      : user.type == TYPES.OTHER
                        ? t("other")
                        : user.type == TYPES.SCHOOLTEACHER
                          ? t("schoolTeacher")
                          : "-"}
                </p>
              )}
              {isEditing && (
                <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
              )}
            </div>
            {/* University */}
            {profileFormik.values.studentType == TYPES.UNIVERSITY && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("universityLabel")}</label>
                  <ErrorMsg name="university" />
                </div>

                {isEditing ? (
                  <select
                    name="university"
                    value={profileFormik.values.university}
                    onChange={(e) => profileFormik.setFieldValue("university", Number(e.target.value))}
                    onBlur={profileFormik.handleBlur}
                    className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                  >
                    <option value="" disabled>
                      {t("universityPlaceholder")}
                    </option>

                    {UNIVERSITIES.map((uni) => (
                      <option key={uni.id} value={uni.id}>
                        {t(`universities.${uni.id}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-gray-800">
                    {profileFormik.values.university
                      ? t(`universities.${profileFormik.values.university}`)
                      : "-"}
                  </p>
                )}

                {isEditing && (
                  <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
                )}
              </div>
            )}
            {/* universityName */}
            {profileFormik.values.studentType == TYPES.UNIVERSITY &&
              profileFormik.values.university == 13 && (
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <label className="block text-sm font-bold text-gray-600">
                      {t("universityOtherPlaceholder")}
                    </label>
                    <ErrorMsg name="universityName" />
                  </div>

                  {isEditing ? (
                    <input
                      type="text"
                      name="universityName"
                      value={profileFormik.values.universityName}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      className="w-full rounded-xl border bg-gray-100 p-2"
                    />
                  ) : (
                    <p className="mt-1 text-gray-800">{profileFormik.values.universityName || "-"}</p>
                  )}
                </div>
              )}
            {/* fieldOfStudy */}
            {(profileFormik.values.studentType == TYPES.UNIVERSITY ||
              profileFormik.values.studentType == TYPES.OTHER) && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("fieldOfStudyLabel")}</label>
                  <ErrorMsg name="fieldOfStudy" />
                </div>

                {isEditing ? (
                  <select
                    name="fieldOfStudy"
                    value={profileFormik.values.fieldOfStudy}
                    onChange={(e) => profileFormik.setFieldValue("fieldOfStudy", Number(e.target.value))}
                    onBlur={profileFormik.handleBlur}
                    className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                  >
                    <option value="" disabled>
                      {t("fieldOfStudyPlaceholder")}
                    </option>

                    {FIELDS.map((field) => (
                      <option key={field.id} value={field.id}>
                        {t(`fields.${field.id}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-gray-800">
                    {profileFormik.values.fieldOfStudy
                      ? t(`fields.${profileFormik.values.fieldOfStudy}`)
                      : "-"}
                  </p>
                )}

                {isEditing && (
                  <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
                )}
              </div>
            )}
            {profileFormik.values.fieldOfStudy == 12 &&
              profileFormik.values.studentType == TYPES.UNIVERSITY && (
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <label className="block text-sm font-bold text-gray-600">
                      {t("fieldOfStudyOtherPlaceholder")}
                    </label>
                  </div>

                  {isEditing ? (
                    <input
                      type="text"
                      name="fieldOfStudyOther"
                      value={profileFormik.values.fieldOfStudyOther}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      className="w-full rounded-xl border bg-gray-100 p-2"
                    />
                  ) : (
                    <p className="mt-1 text-gray-800">{profileFormik.values.fieldOfStudyOther || "-"}</p>
                  )}
                </div>
              )}

            {/* employmentStatus */}
            {profileFormik.values.studentType == TYPES.OTHER && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">
                    {t("employmentStatusLabel")}
                  </label>
                  <ErrorMsg name="employmentStatus" />
                </div>

                {isEditing ? (
                  <select
                    name="employmentStatus"
                    value={profileFormik.values.employmentStatus}
                    onChange={(e) => profileFormik.setFieldValue("employmentStatus", Number(e.target.value))}
                    onBlur={profileFormik.handleBlur}
                    className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                  >
                    <option value="" disabled>
                      {t("employmentStatusPlaceholder")}
                    </option>

                    {EMPLOYMENT_STATUS.map((status) => (
                      <option key={status.id} value={status.id}>
                        {t(`employmentStatus.${status.id}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-gray-800">
                    {profileFormik.values.employmentStatus
                      ? t(`employmentStatus.${profileFormik.values.employmentStatus}`)
                      : "-"}
                  </p>
                )}

                {isEditing && (
                  <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
                )}
              </div>
            )}
            {/* employmentStatusOther */}
            {profileFormik.values.employmentStatus == 3 &&
              profileFormik.values.studentType == TYPES.OTHER && (
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <label className="block text-sm font-bold text-gray-600">
                      {t("employmentStatusOtherPlaceholder")}
                    </label>
                    <ErrorMsg name="employmentStatusOther" />
                  </div>

                  {isEditing ? (
                    <input
                      type="text"
                      name="employmentStatusOther"
                      value={profileFormik.values.employmentStatusOther}
                      onChange={profileFormik.handleChange}
                      onBlur={profileFormik.handleBlur}
                      className="w-full rounded-xl border bg-gray-100 p-2"
                    />
                  ) : (
                    <p className="mt-1 text-gray-800">{profileFormik.values.employmentStatusOther || "-"}</p>
                  )}
                </div>
              )}
            {/* sector */}
            {profileFormik.values.studentType == TYPES.OTHER &&
              profileFormik.values.employmentStatus == 1 && (
                <div className="relative">
                  <div className="mb-1 flex items-center gap-2">
                    <label className="block text-sm font-bold text-gray-600">{t("sectorLabel")}</label>
                    <ErrorMsg name="sector" />
                  </div>
                  {isEditing ? (
                    <select
                      name="sector"
                      value={profileFormik.values.sector}
                      onChange={(e) => profileFormik.setFieldValue("sector", e.target.value)}
                      onBlur={profileFormik.handleBlur}
                      className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                    >
                      <option value="" disabled>
                        {t("sectorPlaceholder")}
                      </option>
                      {SECTORS.map((sector) => (
                        <option key={sector.id} value={sector.id}>
                          {t(`sectors.${sector.id}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="mt-1 text-gray-800">
                      {user.sector_id ? t(`sectors.${user.sector_id}`) : "-"}
                    </p>
                  )}
                  {isEditing && (
                    <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
                  )}
                </div>
              )}
            {/* educationLevel */}
            {profileFormik.values.studentType == TYPES.OTHER && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("educationLevelLabel")}</label>
                  <ErrorMsg name="educationLevel" />
                </div>
                {isEditing ? (
                  <select
                    name="educationLevel"
                    value={profileFormik.values.educationLevel}
                    onChange={(e) => profileFormik.setFieldValue("educationLevel", e.target.value)}
                    onBlur={profileFormik.handleBlur}
                    className="w-full appearance-none rounded-xl border bg-gray-100 p-2"
                  >
                    <option value="" disabled>
                      {t("educationLevelPlaceholder")}
                    </option>
                    {EDUCATION_LEVELS.map((level) => (
                      <option key={level.id} value={level.id}>
                        {t(`educationLevels.${level.id}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-gray-800">
                    {user.education_level_id ? t(`educationLevels.${user.education_level_id}`) : "-"}
                  </p>
                )}
                {isEditing && (
                  <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
                )}
              </div>
            )}

            {profileFormik.values.studentType == TYPES.SCHOOLTEACHER && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("ministerialIdLabel")}</label>
                  <ErrorMsg name="ministerialID" />
                </div>

                {isEditing ? (
                  <input
                    type="text"
                    name="ministerialID"
                    value={profileFormik.values?.ministerialID}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // only digits
                      profileFormik.setFieldValue("ministerialID", value);
                    }}
                    onBlur={profileFormik.handleBlur}
                    className="w-full rounded-xl border bg-gray-100 p-2"
                  />
                ) : (
                  <p className="mt-1 text-gray-800">{user?.ministerial_id}</p>
                )}
              </div>
            )}
            {/* Nationality */}
            <div className="relative">
              <div className="mb-1 flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-600">{t("nationalityLabel")}</label>
                <ErrorMsg name="nationality" />
              </div>{" "}
              {isEditing ? (
                <select
                  readOnly
                  disabled
                  name="nationality"
                  value={profileFormik.values.nationality}
                  onChange={(e) => profileFormik.setFieldValue("nationality", Number(e.target.value))}
                  onBlur={profileFormik.handleBlur}
                  className="w-full appearance-none rounded-xl border bg-[#d9dbe0] p-2"
                >
                  <option value="" disabled>
                    {t("nationalityPlaceholder")}
                  </option>
                  <option value={NATIONALITIES.JORDAN}>{t("jordanian")}</option>
                  <option value={NATIONALITIES.SYRIA}>{t("syrian")}</option>
                  <option value={NATIONALITIES.OTHER}>{t("other")}</option>
                </select>
              ) : (
                <p className="mt-1 text-gray-800">
                  {user.nationality_id == NATIONALITIES.JORDAN
                    ? t("jordanian")
                    : user.nationality_id == NATIONALITIES.SYRIA
                      ? t("syrian")
                      : t("other")}
                </p>
              )}
              {/* {isEditing && (
                <FaChevronDown className="pointer-events-none absolute top-9 text-gray-500 ltr:right-4 rtl:left-4" />
              )} */}
            </div>
            {/* National ID (Jordanian) */}
            {profileFormik.values.nationality == NATIONALITIES.JORDAN && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("nationalNumberLabel")}</label>
                  <ErrorMsg name="nationalNumber" />
                </div>

                {isEditing ? (
                  <input
                    type="text"
                    name="nationalNumber"
                    value={profileFormik.values.nationalNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // only digits
                      profileFormik.setFieldValue("nationalNumber", value);
                    }}
                    onBlur={profileFormik.handleBlur}
                    maxLength={10}
                    className="w-full rounded-xl border bg-gray-100 p-2"
                  />
                ) : (
                  <p className="mt-1 text-gray-800">{user.national_id}</p>
                )}
              </div>
            )}

            {/* Document ID (Syrian) */}
            {profileFormik.values.nationality == NATIONALITIES.SYRIA && (
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("documentIdLabel")}</label>
                  <ErrorMsg name="documentId" />
                </div>
                {isEditing ? (
                  <input
                    readOnly
                    disabled
                    type="text"
                    name="documentId"
                    value={profileFormik.values.documentId}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // only digits
                      profileFormik.setFieldValue("documentId", value);
                    }}
                    onBlur={profileFormik.handleBlur}
                    maxLength={10}
                    className="w-full rounded-xl border bg-gray-300 p-2"
                  />
                ) : (
                  <p className="mt-1 text-gray-800">{user.national_id}</p>
                )}
              </div>
            )}
          </div>
        </form>
        {/* Account Settings */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <div className="space-y-3">
            <button
              onClick={() => setShowChangePassword(true)}
              className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-4 py-3 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            >
              <FaLock /> {t("changePassword")}
            </button>
          </div>
        </div>
      </div>
      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 top-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            {serverMessage && (
              <div
                className={`mb-2 rounded-lg px-3 py-2 text-sm ${
                  serverMessage.startsWith("✅")
                    ? "border border-green-300 bg-green-100 text-green-700"
                    : "border border-red-300 bg-red-100 text-red-700"
                }`}
              >
                {serverMessage}
              </div>
            )}

            <form onSubmit={passwordFormik.handleSubmit} className="space-y-3">
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("currentPassword")}</label>
                  {passwordFormik.touched.current_password && passwordFormik.errors.current_password && (
                    <p className="text-sm text-red-500">{passwordFormik.errors.current_password}</p>
                  )}
                </div>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  name="current_password"
                  value={passwordFormik.values.current_password}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2 text-gray-800"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute top-11 -translate-y-1/2 text-gray-600 ltr:right-3 rtl:left-3"
                >
                  {showCurrentPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("newPassword")}</label>
                  {passwordFormik.touched.new_password && passwordFormik.errors.new_password && (
                    <p className="text-sm text-red-500">{passwordFormik.errors.new_password}</p>
                  )}
                </div>
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="new_password"
                  value={passwordFormik.values.new_password}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2 text-gray-800"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute top-11 -translate-y-1/2 text-gray-600 ltr:right-3 rtl:left-3"
                >
                  {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-gray-600">{t("confirmNewPassword")}</label>
                  {passwordFormik.touched.new_password_confirmation &&
                    passwordFormik.errors.new_password_confirmation && (
                      <p className="text-sm text-red-500">
                        {passwordFormik.errors.new_password_confirmation}
                      </p>
                    )}
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="new_password_confirmation"
                  value={passwordFormik.values.new_password_confirmation}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  className="w-full rounded-xl border bg-gray-100 p-2 text-gray-800"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute top-11 -translate-y-1/2 text-gray-600 ltr:right-3 rtl:left-3"
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    passwordFormik.resetForm();
                    setServerMessage("");
                  }}
                  className="rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-300"
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  disabled={passwordFormik.isSubmitting}
                  className={`flex w-32 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm transition ${
                    passwordFormik.isSubmitting
                      ? "cursor-not-allowed bg-purple-400 text-white"
                      : "bg-purple-600 text-white hover:bg-purple-500"
                  }`}
                >
                  {passwordFormik.isSubmitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                    </>
                  ) : (
                    t("save")
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Change Password Modal */}
      {showCertificatesModal && (
        <div className="fixed inset-0 top-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            {/* Header */}
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-800">{t("downloadCertificates")}</h2>
              </div>

              <button
                onClick={() => setShowCertificatesModal(false)}
                className="rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>

            {/* Certificates */}
            <div className="space-y-3">
              {certificates.length > 0 ? (
                certificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="group flex items-center justify-between rounded-xl border bg-gray-50 px-4 py-3 transition hover:border-purple-300 hover:bg-purple-50"
                  >
                    <div className="flex items-center gap-3">
                      <FaAward className="text-purple-500" />
                      <span className="font-medium text-gray-700">{cert.event_name}</span>
                    </div>

                    <button
                      onClick={() => handleDownloadCertificate(cert.id)}
                      disabled={downloadingId == cert.id}
                      className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                        downloadingId == cert.id
                          ? "cursor-not-allowed bg-purple-400 text-white"
                          : "bg-purple-600 text-white hover:bg-purple-500"
                      } `}
                    >
                      {downloadingId == cert.id ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                          {t("downloading")}
                        </>
                      ) : (
                        <>
                          <FaDownload />
                          {t("download")}
                        </>
                      )}
                    </button>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
                  <FaCertificate className="text-3xl" />
                  <p className="text-sm">{t("noCertificates")}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowCertificatesModal(false)}
                className="rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-300"
              >
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default Profile;
